const RATIO_TOLERANCE = 0.01; // same value in artwork-composite.liquid
class ArtworkSelector extends HTMLElement {
  #abort = null;
  #form = null;
  #matrix = null;
  #data = null;
  #sel = [];
  #axis = { size: -1, material: -1, frame: -1 };
  #composites = [];
  #live = null;
  #section = null;
  #reconcileTimer = null;
  #currentRow = null;
  #sticky = null;
  #io = null;
  #paintToken = 0;
  #host = 'pdp';
  #hide = true;
  #prefetchToken = 0;
  #prefetchIdle = null;
  #intentPending = null;
  #intentRunning = false;
  #speculation = new Set();

  connectedCallback() {
    const productId = this.getAttribute('product-id');
    const formId = this.getAttribute('form-id');
    this.#form = document.getElementById(formId);
    this.#host = this.getAttribute('host') || 'pdp';
    // hide (default): a value the selected material never sells has no row or tile; grey: every
    // unavailable value is greyed.
    this.#hide = this.getAttribute('unavailable-display') !== 'grey';
    this.#matrix = this.#readJson(`script[data-artwork-matrix="${productId}"]`);
    this.#data = this.#readJson(`script[data-artwork-composite="${productId}"]`) || { orientation: 'portrait', frames: [], art: [] };
    if (!this.#matrix || !this.#form) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const names = this.#matrix.n.map((n) => n.toLowerCase());
    this.#axis = {
      size: names.indexOf('size'),
      material: names.indexOf('material'),
      frame: this.#matrix.f
    };
    this.#composites = [...document.querySelectorAll(`artwork-composite[data-product="${productId}"]`)];
    this.#live = this.querySelector('[data-artwork-live]');
    this.#section = this.closest('.shopify-section') || this.#form.closest('.shopify-section');

    const initialId = this.#form.id?.value;
    this.#currentRow = this.#matrix.r.find((row) => String(row[this.#matrix.n.length]) === String(initialId))
      || this.#matrix.r.find((row) => row[this.#matrix.n.length + 1] === 1)
      || this.#matrix.r[0];
    this.#sel = this.#currentRow.slice(0, this.#matrix.n.length);
    // The page loads with no size chosen (the server rendered the radios that way): add to
    // cart waits, the price reads "From", the image shows the smallest size. A ?variant= link is
    // a full selection.
    if (this.hasAttribute('size-unselected') && this.#axis.size > -1 && !new URL(window.location.href).searchParams.get('variant')) {
      this.#sel[this.#axis.size] = -1;
      this.#currentRow = null;
    }
    // The sheet mirrors the page: an empty form id means no size chosen, so the sheet shows none
    // either and its first tap on any size is a real choice.
    if (this.#host !== 'pdp' && this.#axis.size > -1 && !initialId) {
      this.#sel[this.#axis.size] = -1;
      this.#currentRow = null;
    }

    this.addEventListener('change', this.#onChange, { signal });
    for (const type of ['pointerover', 'pointerdown', 'focusin']) this.addEventListener(type, this.#onIntent, { signal, passive: true });
    this.addEventListener('click', this.#onClick, { signal, capture: true });
    // Every instance follows the form's id input (the AR sheet is a second instance).
    this.#form.addEventListener('change', (e) => { if (e.target === this.#form.id) this.#syncFromForm(); }, { signal });
    const toggle = this.querySelector('[data-toggle]');
    const upperGuide = this.hasAttribute('keep-selector-button-position') ? this.querySelector('.artwork-selector__size-guide--bar') : null;
    upperGuide?.toggleAttribute('inert', this.classList.contains('is-open'));
    toggle?.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open);
      upperGuide?.toggleAttribute('inert', open);
      if (open) this.querySelector('input:checked')?.focus({ preventScroll: true });
    }, { signal });
    this.#sticky = this.querySelector('[data-sticky]');
    // The card shows once the picker's button (phones, which have no button: the buy button)
    // has scrolled above the viewport, and it is the way back: it scrolls to the info column
    // and opens the picker.
    const disclosure = this.querySelector('.artwork-selector__disclosure');
    const buy = this.#section?.querySelector('[data-block-type="buy-buttons"]');
    const anchor = (window.matchMedia('(min-width: 1000px)').matches && disclosure) ? disclosure : buy;
    if (this.#sticky && anchor) {
      // A scroll listener, not an observer: an observer misses a jump that never intersects (a fast
      // flick on a phone lands with the anchor already above the viewport and no callback).
      let ticking = false;
      const update = () => { ticking = false; const shown = anchor.getBoundingClientRect().bottom < 0; this.#sticky.setAttribute('aria-hidden', String(!shown)); this.#sticky.toggleAttribute('inert', !shown); };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', onScroll, { passive: true, signal });
      update();
      this.#sticky.addEventListener('click', () => {
        const target = this.#section?.querySelector('.product-info') || this;
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        if (toggle && toggle.offsetParent !== null && !this.classList.contains('is-open')) toggle.click();
      }, { signal });
    }
    this.#paint();
    if (!this.#currentRow && this.#host === 'pdp') this.#commitUnselected();
  }

  // Follow the form id another instance wrote; never re-dispatches.
  #syncFromForm() {
    const id = String(this.#form.id?.value || '');
    if (!id || (this.#currentRow && String(this.#rowId(this.#currentRow)) === id)) return;
    const row = this.#rows().find((r) => String(this.#rowId(r)) === id);
    if (!row) return;
    this.#sel = row.slice(0, this.#axisCount());
    this.#currentRow = row;
    this.#paint({ returnToGallery: true });
    if (this.#host === 'pdp') {
      this.#paintPrice(row);
      this.#paintBuyButton(row);
      const stickyPrice = this.#sticky?.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.#rowPrice(row);
      this.#reconcileIfNeeded(this.#rowId(row));
    }
  }

  // AR sheet: ready sizes per the viewer's own hasAssetFor, exposed on the host.
  #markAr() {
    const has = this.closest('.ar-view-in-room')?.vrpHasAssetFor;
    const { size } = this.#axis;
    if (typeof has !== 'function' || size < 0) return;
    const label = document.querySelector('[data-ar-launch-label]')?.getAttribute('data-ar-launch-label') || '';
    for (const input of this.querySelectorAll(`input[type="radio"][data-axis="${size}"]`)) {
      const trial = this.#sel.slice(); trial[size] = Number(input.value);
      const row = this.#rows().find((r) => this.#matches(r, trial));
      const ready = !!row && has(String(this.#rowId(row)));
      const el = input.nextElementSibling;
      if (!el) continue;
      el.toggleAttribute('data-ar-ready', ready);
      let text = el.querySelector('.artwork-selector__ar');
      if (ready && label) {
        if (!text) { text = document.createElement('span'); text.className = 'artwork-selector__ar sr-only'; el.querySelector('.artwork-selector__label')?.append(text); }
        text.textContent = ` ${label}`;
      } else text?.remove();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#io?.disconnect();
    clearTimeout(this.#reconcileTimer);
    this.#cancelPrefetch();
    this.#intentPending = null;
    ++this.#paintToken;
  }

  get selection() {
    return this.#sel.slice();
  }

  #readJson(selector) {
    const node = document.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch { return null; }
  }

  #onClick = (event) => {
    const control = event.target.closest('label, input[type="radio"]');
    if (!control) return;
    const input = control.matches('input') ? control : control.control;
    if (input?.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
    // A checked radio does not dispatch change, but should still reveal its preview.
    if (input?.checked && this.#host === 'pdp') this.#paintComposites(true);
  };

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.dataset.axis) return;
    const axis = Number(input.dataset.axis);
    const value = Number(input.value);
    if (input.getAttribute('aria-disabled') === 'true') {
      this.#checkRadio(axis, this.#sel[axis]);
      return;
    }
    this.#select(axis, value);
  };

  #rows() { return this.#matrix.r; }
  #axisCount() { return this.#matrix.n.length; }
  #rowId(row) { return row[this.#axisCount()]; }
  #rowAvailable(row) { return row[this.#axisCount() + 1] === 1; }
  #rowPrice(row) { return row[this.#axisCount() + 2]; }
  #rowCompareAt(row) { return row[this.#axisCount() + 3] || null; }

  #matches(row, sel, skip = -1) {
    return sel.every((v, k) => k === skip || row[k] === v);
  }

  #validValues(axis, selection = this.#sel) {
    const { material, size } = this.#axis;
    const set = new Set();
    for (const row of this.#rows()) {
      if (material > -1 && axis !== material && row[material] !== selection[material]) continue;
      if (size > -1 && axis !== size && axis !== material && selection[size] !== -1 && row[size] !== selection[size]) continue;
      set.add(row[axis]);
    }
    return set;
  }

  // The values the selected material offers on an axis, whatever the size: the set hide mode
  // keeps visible. A value in here but not in #validValues is a size-level gap, greyed.
  #offered(axis, selection = this.#sel) {
    const { material } = this.#axis;
    const set = new Set();
    for (const row of this.#rows()) {
      if (material > -1 && axis !== material && row[material] !== selection[material]) continue;
      set.add(row[axis]);
    }
    return set;
  }

  #targetAr(dims) {
    if (!(dims && dims.length === 2 && dims[0] > 0 && dims[1] > 0)) return null;
    if (dims[0] === dims[1]) return 1;
    return (this.#data.orientation || 'portrait') === 'landscape' ? dims[1] / dims[0] : dims[0] / dims[1];
  }

  #select(axis, value) {
    const previousSel = this.#sel.slice();
    const previousRow = this.#currentRow;
    this.#sel[axis] = value;
    const { size, material, frame } = this.#axis;
    const notes = [];

    if (axis === material && size > -1 && this.#sel[size] !== -1) {
      const valid = this.#validValues(size);
      if (!valid.has(this.#sel[size])) {
        const from = this.#sel[size];
        this.#sel[size] = this.#nearestByArea(from, valid);
        notes.push(this.#fill(this.dataset.sizeChanged, { size: this.#value(size, this.#sel[size]), previous: this.#value(size, from), material: this.#value(material, value) }));
      }
    }

    if (frame > -1 && axis !== frame) {
      const valid = this.#validValues(frame);
      if (!valid.has(this.#sel[frame])) {
        const from = this.#sel[frame];
        this.#sel[frame] = [...valid].sort((a, b) => a - b)[0] ?? this.#sel[frame];
        if (this.#sel[frame] !== from) {
          // A frame the new material never sells is named as a material change, not a size change;
          // a frame the material sells but not at this size keeps the size wording.
          const byMaterial = axis === material && !this.#offered(frame).has(from);
          notes.push(byMaterial
            ? this.#fill(this.dataset.frameChangedMaterial, { frame: this.#value(frame, this.#sel[frame]), previous: this.#value(frame, from), material: this.#value(material, value) })
            : this.#fill(this.dataset.frameChanged, { frame: this.#value(frame, this.#sel[frame]), previous: this.#value(frame, from) }));
        }
      }
    }

    if (size > -1 && this.#sel[size] === -1) {
      // Still no size: the other axes move, the preview follows, add to cart keeps waiting.
      this.#paint({ returnToGallery: true });
      if (notes.length) this.#announce(notes.join(' '));
      if (this.#host === 'pdp') this.#commitUnselected();
      return;
    }
    let row = this.#rows().find((r) => this.#matches(r, this.#sel));
    if (!row && frame > -1) {
      row = this.#rows().find((r) => this.#matches(r, this.#sel, frame));
      if (row && row[frame] !== this.#sel[frame]) {
        notes.push(this.#fill(this.dataset.frameChanged, { frame: this.#value(frame, row[frame]), previous: this.#value(frame, this.#sel[frame]) }));
        this.#sel[frame] = row[frame];
      }
    }
    if (!row) {
      this.#sel = previousSel;
      this.#paint();
      return;
    }
    this.#currentRow = row;
    this.#paint({ returnToGallery: true });
    if (notes.length) this.#announce(notes.join(' '));
    setTimeout(() => this.#commit(row, previousRow), 0);
  }

  #value(axis, index) {
    return axis > -1 ? this.#matrix.v[axis][index] : '';
  }

  #fill(template, values) {
    return Object.entries(values).reduce((t, [k, v]) => t.split(`{{ ${k} }}`).join(v), template || '');
  }

  #nearestByArea(fromIndex, validSet) {
    const d = this.#matrix.d;
    const area = (i) => (d[i] && d[i].length === 2 ? d[i][0] * d[i][1] : 0);
    const target = area(fromIndex);
    let best = null;
    let bestDelta = Infinity;
    for (const i of validSet) {
      const delta = Math.abs(area(i) - target);
      if (delta < bestDelta) { bestDelta = delta; best = i; }
    }
    return best;
  }

  #announce(text) {
    if (!this.#live) return;
    this.#live.textContent = '';
    requestAnimationFrame(() => { this.#live.textContent = text; });
  }

  #checkRadio(axis, value) {
    if (value === -1) {
      for (const input of this.querySelectorAll(`input[type="radio"][data-axis="${axis}"]:checked`)) input.checked = false;
      return;
    }
    const input = this.querySelector(`input[type="radio"][data-axis="${axis}"][value="${value}"]`);
    if (input && !input.checked) input.checked = true;
  }

  // The smallest size sold in the current material and frame: what the image shows while no
  // size is chosen.
  #previewSize(selection = this.#sel) {
    const { size } = this.#axis;
    const d = this.#matrix.d;
    let best = null;
    let bestArea = Infinity;
    for (const i of this.#validValues(size, selection)) {
      const area = d[i] && d[i].length === 2 ? d[i][0] * d[i][1] : Infinity;
      if (area < bestArea) { bestArea = area; best = i; }
    }
    return best ?? 0;
  }

  #fromPrice() {
    const { size } = this.#axis;
    let best = null;
    let bestValue = Infinity;
    for (const row of this.#rows()) {
      if (!this.#matches(row, this.#sel, size)) continue;
      const value = Number(String(this.#rowPrice(row)).replace(/[^\d.]/g, ''));
      if (value < bestValue) { bestValue = value; best = row; }
    }
    return best ? this.#fill(this.dataset.fromPrice, { price: this.#rowPrice(best) }) : '';
  }

  #commitUnselected() {
    const scope = this.#section || document;
    const from = this.#fromPrice();
    for (const node of scope.querySelectorAll('[data-block-type="price"] sale-price')) { node.textContent = from; node.classList.add('text-subdued', 'is-from'); node.classList.remove('text-on-sale'); }
    for (const node of scope.querySelectorAll('[data-block-type="price"] compare-at-price')) node.hidden = true;
    const button = scope.querySelector('[data-block-type="buy-buttons"] button[type="submit"]');
    if (button) {
      button.disabled = true;
      const label = button.querySelector('[data-button-label]') || button.querySelector('span:not(.sr-only)') || button;
      if (this.dataset.chooseOptions) label.textContent = this.dataset.chooseOptions;
    }
    const stickyPrice = this.#sticky?.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = from;
    if (this.#form.id) this.#form.id.value = '';
  }

  #paint({ returnToGallery = false } = {}) {
    const n = this.#axisCount();
    const { size, material, frame } = this.#axis;
    for (let k = 0; k < n; k++) {
      this.#checkRadio(k, this.#sel[k]);
      const shown = this.querySelector(`[data-selected-value="${k}"]`);
      if (shown) shown.textContent = this.#sel[k] === -1 ? '' : this.#matrix.v[k][this.#sel[k]];
      const valid = this.#validValues(k);
      // Hide mode: the material's offered set decides who has a row or tile; the tile count
      // follows it so the visible frames fill the row. Grey mode leaves the DOM as before.
      // Only when a Material axis exists (mirrors the server render, which computes the offered set only for material_index > -1).
      const offered = this.#hide && material > -1 && k !== material ? this.#offered(k) : null;
      if (offered) this.querySelector(`fieldset[data-axis="${k}"] .variant-picker__option-values`)?.style.setProperty('--tiles', offered.size);
      for (const input of this.querySelectorAll(`input[type="radio"][data-axis="${k}"]`)) {
        const j = Number(input.value);
        const exists = valid.has(j);
        const hide = !!offered && !offered.has(j);
        const greyed = !exists && !hide;
        input.setAttribute('aria-disabled', exists ? 'false' : 'true');
        const trial = this.#sel.slice(); trial[k] = j;
        const row = this.#rows().find((r) => this.#matches(r, trial));
        const soldOut = exists && row ? !this.#rowAvailable(row) : false;
        const label = input.nextElementSibling;
        input.hidden = hide;
        if (label) label.hidden = hide;
        label?.classList.toggle('is-sold-out', soldOut);
        label?.classList.toggle('is-disabled', greyed);
        const why = document.getElementById(`${input.id}-why`);
        if (why) {
          why.hidden = !greyed;
          why.textContent = !greyed ? '' : (k === frame && size > -1 && this.#sel[size] !== -1
            ? this.#fill(this.dataset.unavailableForSize, { value: this.#value(k, j), size: this.#value(size, this.#sel[size]) })
            : this.#fill(this.dataset.unavailable, { value: this.#value(k, j), material: this.#value(material, this.#sel[material]) }));
          // The reason's accessible path is the description on the input; the label's title is a
          // pointer-only supplement.
          if (!greyed) { input.removeAttribute('aria-describedby'); label?.removeAttribute('title'); }
          else { input.setAttribute('aria-describedby', why.id); if (label && k !== frame) label.title = why.textContent; }
        }
        if (k === size) {
          const priceNode = label?.querySelector('[data-size-price]');
          if (priceNode) {
            const priced = row || this.#rows().find((r) => r[size] === j && (material < 0 || r[material] === this.#sel[material]));
            priceNode.textContent = priced ? this.#rowPrice(priced) : '';
          }
        }
      }
    }
    if (this.#host === 'pdp') this.#paintComposites(returnToGallery);
    if (this.#host === 'ar-drawer') this.#markAr();
  }

  #paintComposites(returnToGallery = false) {
    this.#composites = [...document.querySelectorAll(`artwork-composite[data-product="${this.getAttribute('product-id')}"]`)];
    if (!this.#composites.length) return;
    const attrs = this.#compositeAttrs(this.#sel);
    const token = ++this.#paintToken;
    this.#cancelPrefetch();
    this.#intentPending = null;
    // Modules may finish in either order. The renderer owns pair readiness and stale work.
    customElements.whenDefined('artwork-composite').then(() => {
      if (!this.isConnected || token !== this.#paintToken) return;
      const primary = this.#galleryComposite();
      for (const composite of this.#composites) {
        const ready = composite.renderVariant(attrs);
        if (composite !== primary) continue;
        ready.then((committed) => {
          if (!committed || !this.isConnected || token !== this.#paintToken) return;
          if (returnToGallery) this.#returnToGallery(primary);
          this.#schedulePrefetch();
        });
      }
    });
  }

  #galleryComposite() {
    return this.#composites.find((c) => c.classList.contains('product-gallery__composite'));
  }

  #returnToGallery(composite) {
    const cell = composite.closest('.product-gallery__media--composite');
    const gallery = cell?.closest('product-gallery');
    const index = gallery?.carousel?.cells.indexOf(cell) ?? -1;
    if (index >= 0 && gallery.carousel.selectedIndex !== index) gallery.carousel.select(index, { instant: true });
  }

  #compositeAttrs(selection) {
    const { size, material, frame } = this.#axis;
    const v = this.#matrix.v;
    const materialValue = material > -1 ? v[material][selection[material]] : null;
    const frameValue = frame > -1 ? v[frame][selection[frame]] : null;
    const dims = size > -1 ? this.#matrix.d[selection[size] === -1 ? this.#previewSize(selection) : selection[size]] : null;
    const diagnostics = [];
    const list = this.#artList(materialValue);
    const targetAr = this.#targetAr(dims) ?? list[0]?.ar ?? 1;

    let art = this.#nearest(list, (a) => a.ar, targetAr);
    // Among files within the tolerance, the first whose name carries the size wins
    // (artwork-size-match.liquid is the same rule); with no such file, the nearest.
    if (dims) art = list.find((a) => Math.abs(a.ar - targetAr) <= RATIO_TOLERANCE && ArtworkSelector.#sizeMatch(a.n, dims)) || art;
    if (!list.length) diagnostics.push('no-artwork-sources');
    else if (art && Math.abs(art.ar - targetAr) > RATIO_TOLERANCE) diagnostics.push('no-artwork-for-aspect');
    if (art?.dg) diagnostics.push(art.dg); // canvas-wrap-unconfirmed, same order as artwork-composite.liquid

    let record = null;
    if (frameValue !== null) {
      const carrying = this.#data.frames.filter((f) => f.ov.includes(frameValue));
      if (materialValue !== null) record = carrying.find((f) => !f.ma.length || f.ma.includes(materialValue)) || null;
      else if (carrying.length === 1) record = carrying[0];
      else { // no Material option: `im` (the tags) must leave exactly one record
        const tagged = carrying.filter((f) => f.ma.some((m) => (this.#data.im || []).includes(m)));
        record = tagged.length === 1 ? tagged[0] : null;
      }
      if (!record) diagnostics.push('no-frame-record');
    }
    let mode = record ? record.rm : 'flat';
    let overlay = null;
    if (record && mode === 'overlay_image') {
      // The nearest render is drawn even off-ratio: a frame the customer pays for is never invisible.
      overlay = this.#nearest(record.o, (o) => o.r, targetAr);
      if (!overlay) mode = 'flat';
      if (!overlay || Math.abs(overlay.r - targetAr) > RATIO_TOLERANCE) diagnostics.push('no-overlay-for-ratio');
    }

    const alt = record && mode === 'overlay_image'
      ? (this.dataset.altFramed || '').replace('{{ frame }}', record.l)
      : (this.dataset.altUnframed || '');
    const attrs = {
      'render-mode': mode, 'art-ar': targetAr.toFixed(4), 'art-alt': alt,
      'size-scale': this.#sizeScale(dims).toFixed(4), diagnostic: diagnostics.length ? diagnostics.join(' ') : null, 'overlay-src': null, 'overlay-srcset': null, 'overlay-width': null, 'overlay-height': null
    };
    if (art) { attrs['art-src'] = art.s; attrs['art-srcset'] = art.ss || null; attrs['art-name'] = art.n || null; attrs['art-width'] = String(art.w); attrs['art-height'] = String(art.h); }
    if (overlay) Object.assign(attrs, { 'overlay-src': overlay.s, 'overlay-srcset': overlay.ss || null, 'overlay-width': overlay.w == null ? null : String(overlay.w), 'overlay-height': overlay.h == null ? null : String(overlay.h), 'overlay-ar': String(overlay.ar), 'inset-l': String(overlay.il), 'inset-r': String(overlay.ir), 'inset-t': String(overlay.it), 'inset-b': String(overlay.ib) });
    return attrs;
  }

  #artList(materialValue) {
    const canvas = this.#data.artc;
    if (!canvas?.length) return this.#data.art;
    return (/canvas/i.test(materialValue || '') || !this.#data.art.length) ? canvas : this.#data.art;
  }

  #sizeScale(dims) {
    if (!(dims && dims.length === 2 && Math.max(dims[0], dims[1]) > 0 && this.#axis.size > -1)) return 1;
    const longest = Math.max(...this.#matrix.d.flat());
    return longest > 0 ? 0.62 + 0.38 * Math.max(dims[0], dims[1]) / longest : 1;
  }

  #nearest(list, key, target) {
    let best = null;
    let bestDelta = Infinity;
    for (const item of list || []) {
      const delta = Math.abs(key(item) - target);
      if (delta < bestDelta) { bestDelta = delta; best = item; }
    }
    return best;
  }

  // Does an artwork file's name carry the size? Same rule as
  // snippets/artwork-size-match.liquid: the name in
  // lower case with '-', '.' and ' ' as '_', wrapped in '_'; each inch value as 24 or 19_75; a
  // match is _<a>x<b>_ in either order. No regular expressions, so the Liquid twin is the same rule.
  static #sizeMatch(name, dims) {
    if (!name || !dims || dims.length !== 2) return false;
    const norm = '_' + String(name).toLowerCase().split('-').join('_').split('.').join('_').split(' ').join('_') + '_';
    const [a, b] = dims.map((n) => String(n).replace('.', '_'));
    return norm.includes(`_${a}x${b}_`) || norm.includes(`_${b}x${a}_`);
  }

  // Exposed for automated tests.
  static sizeMatch(name, dims) { return ArtworkSelector.#sizeMatch(name, dims); }

  #commit(row, previousRow) {
    const form = this.#form;
    const variant = this.#variantObject(row);
    const previousVariant = previousRow ? this.#variantObject(previousRow) : null;

    if (this.#host === 'pdp') { // the sheet's commit reaches these through #syncFromForm
      this.#paintPrice(row);
      this.#paintBuyButton(row);
      const stickyPrice = this.#sticky?.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = variant.price;
    }

    if (form.id) {
      form.id.value = variant.id;
      form.id.dispatchEvent(new Event('change', { bubbles: true }));
    }
    for (const input of (this.#section || document).querySelectorAll('[data-block-type="payment-terms"] [name="id"]')) {
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    form.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { formId: form.getAttribute('id'), variant, previousVariant }
    }));
    if (this.hasAttribute('update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      // Guarded: a third-party history wrapper throws on unconverted variants.
      try { window.history.replaceState({ path: url.toString() }, '', url.toString()); } catch (error) { console.warn('[artwork-selector] replaceState threw', error); }
    }
    window.Shopify?.PaymentButton?.init?.();
    if (this.#host === 'pdp') this.#reconcileIfNeeded(variant.id);
  }

  #variantObject(row) {
    const n = this.#axisCount();
    const options = this.#sel.map((j, k) => this.#matrix.v[k][row[k]] ?? this.#matrix.v[k][j]);
    return {
      id: this.#rowId(row),
      available: this.#rowAvailable(row),
      price: this.#rowPrice(row),
      compare_at_price: this.#rowCompareAt(row),
      title: options.join(' / '),
      options,
      featured_media: null
    };
  }

  #paintPrice(row) {
    const scope = this.#section || document;
    const set = (node, text) => { const label = node.querySelector('.sr-only'); node.textContent = ''; if (label) node.append(label); if (text) node.append(text); };
    for (const node of scope.querySelectorAll('[data-block-type="price"] sale-price')) { set(node, this.#rowPrice(row)); node.classList.remove('is-from'); }
    const compareAt = this.#rowCompareAt(row);
    for (const node of scope.querySelectorAll('[data-block-type="price"] compare-at-price')) { set(node, compareAt); node.hidden = !compareAt; }
  }

  #paintBuyButton(row) {
    const scope = this.#section || document;
    const button = scope.querySelector('[data-block-type="buy-buttons"] button[type="submit"]');
    if (!button) return;
    const available = this.#rowAvailable(row);
    button.disabled = !available;
    const label = button.querySelector('[data-button-label]') || button.querySelector('span:not(.sr-only)') || button;
    const text = available ? this.dataset.addToCart : this.dataset.soldOut;
    const current = label.textContent.trim();
    if (text && (!current || current === this.dataset.addToCart || current === this.dataset.soldOut || current === this.dataset.chooseOptions)) label.textContent = text;
  }

  #reconcileIfNeeded(variantId) {
    const scope = this.#section;
    if (!scope) return;
    const needy = scope.querySelectorAll('[data-block-type="inventory"], [data-block-type="pickup-availability"], [data-block-type="volume-pricing"], [data-block-type="sku"]');
    if (!needy.length) return;
    clearTimeout(this.#reconcileTimer);
    this.#reconcileTimer = setTimeout(async () => {
      const sectionId = scope.id.replace('shopify-section-', '');
      const url = new URL(window.location.href);
      url.search = '';
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);
      try {
        const html = await fetch(url.toString(), { signal: this.#abort.signal }).then((r) => r.text());
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        for (const block of needy) {
          const fresh = fragment.querySelector(`[data-block-type="${block.dataset.blockType}"]`);
          if (fresh) block.innerHTML = fresh.innerHTML;
        }
        this.dataset.reconciled = String(Number(this.dataset.reconciled || 0) + 1);
      } catch { /* aborted or offline: the page keeps its last known state */ }
    }, 400);
  }

  #canPrefetch(intent = false) {
    if (this.#host !== 'pdp' || !this.isConnected || document.visibilityState === 'hidden') return false;
    const connection = navigator.connection;
    if (connection?.saveData || /2g/.test(connection?.effectiveType || '')) return false;
    return intent || connection?.effectiveType !== '3g';
  }

  // Resolve the same automatic size/frame corrections as a real selection without changing it.
  #trialSelection(axis, value) {
    const selection = this.#sel.slice();
    selection[axis] = value;
    const { size, material, frame } = this.#axis;
    if (axis === material && size > -1 && selection[size] !== -1) {
      const valid = this.#validValues(size, selection);
      if (!valid.has(selection[size])) selection[size] = this.#nearestByArea(selection[size], valid);
    }
    if (frame > -1 && axis !== frame) {
      const valid = this.#validValues(frame, selection);
      if (!valid.has(selection[frame])) selection[frame] = [...valid].sort((a, b) => a - b)[0] ?? selection[frame];
    }
    if (size > -1 && selection[size] === -1) return selection;
    const row = this.#rows().find((r) => this.#matches(r, selection))
      || (frame > -1 && this.#rows().find((r) => this.#matches(r, selection, frame)));
    return row ? row.slice(0, this.#axisCount()) : null;
  }

  #onIntent = (event) => {
    if (!this.#canPrefetch(true)) return;
    const control = event.target.closest('label, input[type="radio"]');
    const input = control?.matches('input') ? control : control?.control;
    if (!input?.dataset.axis || input.checked || input.getAttribute('aria-disabled') === 'true') return;
    const selection = this.#trialSelection(Number(input.dataset.axis), Number(input.value));
    if (!selection) return;
    this.#cancelPrefetch();
    // Keep only the latest intent; it shares the bounded slots with background warming.
    this.#intentPending = { attrs: this.#compositeAttrs(selection), token: this.#prefetchToken };
    this.#drainIntent();
  };

  async #drainIntent() {
    if (this.#intentRunning) return;
    this.#intentRunning = true;
    try {
      const composite = this.#galleryComposite();
      if (!composite || typeof composite.preloadVariant !== 'function' || !await composite.ready) return;
      while (this.#intentPending && this.#canPrefetch(true)) {
        const { attrs, token } = this.#intentPending;
        this.#intentPending = null;
        await this.#preloadPair(composite, attrs, token, true);
      }
    } catch { /* A failed hint never changes the selected visual. */
    } finally {
      this.#intentRunning = false;
    }
  }

  #prefetchConcurrency() {
    return navigator.connection?.effectiveType === '3g' ? 1 : 2;
  }

  async #preloadPair(composite, attrs, token, intent = false) {
    // Two bounded slots avoid a slow artwork blocking every other likely choice.
    // Intent shares these slots; selected-image requests never wait for a slot.
    const current = () => token === this.#prefetchToken && this.#canPrefetch(intent);
    if (!current()) return false;
    while (this.#speculation.size >= this.#prefetchConcurrency()) {
      await Promise.race(this.#speculation);
      if (!current()) return false;
    }
    if (!current()) return false;
    // Reserve synchronously before yielding: simultaneous waiters cannot exceed the limit.
    // Chrome does not reliably promote an in-flight low-priority image on selection.
    // These bounded likely-choice pairs start high only after the visible pair is ready.
    const task = Promise.resolve().then(() => current()
      ? composite.preloadVariant(attrs, { priority: 'high' }) : false).catch(() => false);
    this.#speculation.add(task);
    try { return await task; }
    finally { this.#speculation.delete(task); }
  }

  #cancelPrefetch() {
    ++this.#prefetchToken;
    if (this.#prefetchIdle !== null) {
      if (window.cancelIdleCallback) window.cancelIdleCallback(this.#prefetchIdle);
      else clearTimeout(this.#prefetchIdle);
      this.#prefetchIdle = null;
    }
  }

  #notifyPrepared(token, status) {
    if (token !== this.#prefetchToken || !this.isConnected) return;
    this.dispatchEvent(new CustomEvent('artwork-selector:prepared', {
      bubbles: true, detail: { productId: this.getAttribute('product-id'), status }
    }));
  }

  #schedulePrefetch() {
    this.#cancelPrefetch();
    if (!this.#canPrefetch()) { this.#notifyPrepared(this.#prefetchToken, 'skipped'); return; }
    const token = this.#prefetchToken;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 250));
    this.#prefetchIdle = idle(async () => {
      this.#prefetchIdle = null;
      const composite = this.#galleryComposite();
      if (!composite || token !== this.#prefetchToken) return;
      if (!this.#canPrefetch()) { this.#notifyPrepared(token, 'skipped'); return; }
      const keys = (attrs) => [
        attrs['art-src'] ? `art:${attrs['art-src']}|${attrs['art-srcset'] || ''}` : null,
        attrs['overlay-src'] ? `overlay:${attrs['overlay-src']}|${attrs['overlay-srcset'] || ''}` : null
      ].filter(Boolean);
      const seen = new Set(keys(this.#compositeAttrs(this.#sel)));
      const candidates = [];
      const { frame, size, material } = this.#axis;
      const groups = [];
      for (const axis of new Set([frame, material, size].filter((value) => value > -1))) {
        const values = [...this.#validValues(axis)];
        if (axis === size) values.sort((a, b) => Math.abs(a - this.#sel[size]) - Math.abs(b - this.#sel[size]));
        const group = [];
        for (const value of values) {
          if (value === this.#sel[axis]) continue;
          const selection = this.#trialSelection(axis, value);
          if (selection) group.push(this.#compositeAttrs(selection));
        }
        groups.push(group);
      }
      let newArtwork = 0;
      // Cover frame, material and crop before a second choice on any axis. Artwork is much
      // heavier than a frame: cap automatic warming at four new art sources and six pairs.
      // This covers alternate material plus distinct size crops without starving later sizes.
      while (candidates.length < 6 && groups.some((group) => group.length)) {
        for (const group of groups) {
          while (group.length && candidates.length < 6) {
            const attrs = group.shift();
            const sources = keys(attrs);
            if (sources.every((source) => seen.has(source))) continue;
            const needsArtwork = sources.some((source) => source.startsWith('art:') && !seen.has(source));
            if (needsArtwork && newArtwork >= 4) continue;
            if (needsArtwork) ++newArtwork;
            for (const source of sources) seen.add(source);
            candidates.push(attrs);
            break;
          }
        }
      }
      // Begin only after the first complete visible pair. Two workers share the same slots
      // as intent; a large material image cannot hold every neighboring crop behind it.
      let next = 0;
      const drain = async () => {
        while (next < candidates.length && token === this.#prefetchToken && this.#canPrefetch()) {
          await this.#preloadPair(composite, candidates[next++], token);
        }
      };
      await Promise.all(Array.from({ length: this.#prefetchConcurrency() }, drain));
      this.#notifyPrepared(token, this.#canPrefetch() ? 'complete' : 'skipped');
    }, { timeout: 1500 });
  }

}

if (!customElements.get('artwork-selector')) {
  customElements.define('artwork-selector', ArtworkSelector);
}
